import { WORKSPACE_NAVIGATION_CHANGED_EVENT, WorkspaceNavigationService, type WorkspaceNavigation } from './WorkspaceNavigationService';

// Grants confirmed by the backend draw at once on reload or in a new tab and
// are revalidated behind the screen once stale. A definite denial drops them.
const PREFIX = 'quantis-navigation:';
const FRESH_MS = 5 * 60_000;
const KEEP_MS = 24 * 60 * 60_000;
const LIMIT = 501;
type Entry = { navigation: WorkspaceNavigation; checkedAt: number };
const entries = new Map<string, Entry>();
const requests = new Map<string, Promise<WorkspaceNavigation>>();
const warmRequests = new Map<string, Promise<void>>();
let generation = 0;
export const navigationCacheKey = (actor: string, workspace: string, project?: string | null) =>
  `${PREFIX}${JSON.stringify([actor, workspace, project || ''])}`;

export function cachedNavigation(key: string): WorkspaceNavigation | null {
  let entry = entries.get(key);
  if (!entry) {
    try { entry = JSON.parse(localStorage.getItem(key) || 'null') as Entry | undefined; } catch { /* Storage can be unavailable. */ }
  }
  if (!entry || typeof entry.checkedAt !== 'number' || !Number.isFinite(entry.checkedAt)
    || entry.checkedAt > Date.now() || Date.now() - entry.checkedAt >= KEEP_MS) {
    forget(key);
    return null;
  }
  // Never reuse a record under another actor or scope, including persisted records.
  const n = entry.navigation;
  if (!n || navigationCacheKey(n.userId, n.workspaceId, n.projectId) !== key || !Array.isArray(n.main) || !Array.isArray(n.project)) return null;
  entries.set(key, entry);
  return n;
}

/** True when the cached grants were confirmed recently enough to skip a revalidation. */
export function navigationIsFresh(key: string): boolean {
  const entry = cachedNavigation(key) ? entries.get(key) : undefined;
  return !!entry && Date.now() - entry.checkedAt < FRESH_MS;
}

function storedKeys(): string[] {
  const keys: string[] = [];
  for (let index = 0; index < localStorage.length; index++) {
    const key = localStorage.key(index);
    if (key?.startsWith(PREFIX)) keys.push(key);
  }
  return keys;
}

function forget(key: string): void {
  entries.delete(key);
  try { localStorage.removeItem(key); } catch { /* Optional cache. */ }
}

/** Permission changes fail closed; in-flight responses from before the change cannot repopulate grants. */
export function clearNavigationCache(): void {
  generation++;
  entries.clear();
  requests.clear();
  warmRequests.clear();
  try {
    for (const key of storedKeys()) localStorage.removeItem(key);
  } catch { /* Optional cache. */ }
}

function storeNavigation(navigation: WorkspaceNavigation): void {
  const key = navigationCacheKey(navigation.userId, navigation.workspaceId, navigation.projectId);
  const entry: Entry = { navigation, checkedAt: Date.now() };
  entries.set(key, entry);
  for (const old of [...entries.keys()].slice(0, Math.max(0, entries.size - LIMIT))) entries.delete(old);
  try {
    localStorage.setItem(key, JSON.stringify(entry));
    const keys = storedKeys();
    for (const old of keys.slice(0, Math.max(0, keys.length - LIMIT))) { localStorage.removeItem(old); entries.delete(old); }
  } catch { /* A full or disabled store must not block navigation. */ }
}

/** Prime only backend-returned scoped grants; permission changes discard the entire batch. */
export function warmNavigation(actor: string, workspace: string, projectIds: string[]): Promise<void> {
  const ids = [...new Set(projectIds)].sort().slice(0, 500);
  const key = JSON.stringify([actor, workspace, ids]);
  const pending = warmRequests.get(key);
  if (pending) return pending;
  const request = executeWarmNavigation(actor, workspace, ids);
  warmRequests.set(key, request);
  void request.finally(() => { if (warmRequests.get(key) === request) warmRequests.delete(key); }).catch(() => {});
  return request;
}

async function executeWarmNavigation(actor: string, workspace: string, projectIds: string[]): Promise<void> {
  const ids = [...new Set(projectIds)].slice(0, 500);
  if (navigationIsFresh(navigationCacheKey(actor, workspace)) && ids.every(id => navigationIsFresh(navigationCacheKey(actor, workspace, id)))) return;
  const started = generation;
  const batch = await WorkspaceNavigationService.batch(workspace, ids);
  if (!batch || generation !== started || batch.userId !== actor || batch.workspaceId !== workspace) return;
  const requested = new Set(ids);
  // Validate all rows before storing any part of this result.
  if (batch.projects.some(row => !requested.has(row.projectId) || !Array.isArray(row.items))) return;
  const base = { workspaceId: workspace, userId: actor, main: batch.main, canManage: batch.canManage };
  storeNavigation({ ...base, projectId: null, project: [] });
  batch.projects.forEach(row => storeNavigation({ ...base, projectId: row.projectId, project: row.items }));
}

export function loadNavigation(actor: string, workspace: string, project?: string | null): Promise<WorkspaceNavigation> {
  const key = navigationCacheKey(actor, workspace, project);
  const pending = requests.get(key);
  if (pending) return pending;
  const started = generation;
  const request = WorkspaceNavigationService.resolve(workspace, project).then(navigation => {
    if (started !== generation) throw new Error('Navigation permissions changed');
    if (navigationCacheKey(navigation.userId, navigation.workspaceId, navigation.projectId) !== key) throw new Error('Navigation scope changed');
    storeNavigation(navigation);
    return navigation;
  }, (error: unknown) => {
    if (started === generation && (error as { denied?: boolean })?.denied) forget(key);
    throw error;
  });
  requests.set(key, request);
  void request.finally(() => { if (requests.get(key) === request) requests.delete(key); }).catch(() => {});
  return request;
}

// Clear persisted hints even when no navigation rail is currently mounted.
if (typeof window !== 'undefined') {
  window.addEventListener('quantis:auth-cleared', clearNavigationCache);
  window.addEventListener(WORKSPACE_NAVIGATION_CHANGED_EVENT, clearNavigationCache);
}
